import React, { useEffect } from 'react';
import AppModal from './AppModal';
import { Form, Input, InputNumber, Select } from 'antd';
import { ArrowLeftOutlined, FilterOutlined, PlusOutlined, SearchOutlined, CrownOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import { useOpenMarketLeads } from '@hooks/open-market/useOpenMarketLeads';
import { useOpenMarketUpgrade, isPlanFree, PLAN_CARD_COLORS } from '@hooks/open-market/useOpenMarketUpgrade';
import OpenMarketLeadDetailPanel from './OpenMarketLeadDetailPanel';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    CreateRoleModalBody,
    CreateRoleModalFooter,
    RolesFilterBtn,
    RolesSearchInput,
    RolesSearchWrapper,
    RolesSectionSubtitle,
    RolesSectionTitle,
    RolesSliderTabWrap,
    UpgradeBlock,
    PlanCardsGrid,
    PlanCard,
    PlanCardHeader,
    PlanCardPrice,
    PlanCardPriceUnit,
    PlanCardFeatures,
    PlanCardFeature,
    ErrorBlock,
    DetailPanelLoading,
} from './style';

const OpenMarketUpgradeView = ({ upgradeMessage }) => {
    const {
        showPlans,
        setShowPlans,
        selectedPlanKey,
        setSelectedPlanKey,
        plans,
        plansLoading,
        isPaidPlanSelected,
        handleContinue,
        switchPlanMutation,
    } = useOpenMarketUpgrade(upgradeMessage);

    return (
        <ListBodyLayout title="Open Market" description="Overview of leads and submissions">
            <UpgradeBlock>
                <CrownOutlined className="upgrade-icon" />
                <h2 className="upgrade-title">Upgrade your plan</h2>
                <p className="upgrade-message">{upgradeMessage}</p>
                {!showPlans ? (
                    <Button $primary $blackOnLight $size="14px" $padding="10px 24px" onClick={() => setShowPlans(true)}>
                        Upgrade
                    </Button>
                ) : (
                    <>
                        <PlanCardsGrid>
                            {plansLoading ? (
                                <DetailPanelLoading>Loading plans...</DetailPanelLoading>
                            ) : (
                                plans.map((p, idx) => {
                                    const theme = PLAN_CARD_COLORS[idx % PLAN_CARD_COLORS.length];
                                    const amount = p.afterDiscountAmount ?? p.amount ?? 0;
                                    const isFree = isPlanFree(p);
                                    const features =
                                        Array.isArray(p.features) && p.features.length > 0
                                            ? p.features
                                            : p.details
                                              ? [p.details]
                                              : p.description
                                                ? [p.description]
                                                : [p.name || '—'];
                                    const planKey = p.key || p.id;
                                    const isSelected = selectedPlanKey === planKey;
                                    return (
                                        <PlanCard
                                            key={p.id}
                                            type="button"
                                            $selected={isSelected}
                                            onClick={() => setSelectedPlanKey(planKey)}
                                        >
                                            <PlanCardHeader $color={theme?.header}>
                                                {p.name?.toUpperCase() || 'PLAN'}
                                            </PlanCardHeader>
                                            <PlanCardPrice $color={theme?.text}>
                                                {isFree ? 'FREE' : `$${amount}`}
                                                {!isFree && p.cycle === 'monthly' && (
                                                    <PlanCardPriceUnit> /MO</PlanCardPriceUnit>
                                                )}
                                                {!isFree && p.cycle === 'yearly' && (
                                                    <PlanCardPriceUnit> /YR</PlanCardPriceUnit>
                                                )}
                                            </PlanCardPrice>
                                            <PlanCardFeatures>
                                                {features.map((f, i) => (
                                                    <PlanCardFeature key={i}>{f}</PlanCardFeature>
                                                ))}
                                            </PlanCardFeatures>
                                        </PlanCard>
                                    );
                                })
                            )}
                        </PlanCardsGrid>
                        {isPaidPlanSelected && (
                            <Button
                                $primary
                                $blackOnLight
                                $size="14px"
                                $padding="10px 24px"
                                loading={switchPlanMutation.isPending}
                                onClick={handleContinue}
                            >
                                Continue
                            </Button>
                        )}
                    </>
                )}
            </UpgradeBlock>
        </ListBodyLayout>
    );
};

OpenMarketUpgradeView.defaultProps = { upgradeMessage: 'Upgrade your plan to access Open Market' };

const OpenMarket = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const leadIdFromUrl = searchParams.get('leadId');

    const {
        total,
        openCount,
        closedCount,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        panelOpen,
        closePanel,
        openPanel,
        selectedLeadId,
        getLeadById,
        createModalOpen,
        setCreateModalOpen,
        form,
        bidForm,
        onSubmitCreate,
        onSubmitBid,
        createLeadPending,
        leadsError,
        isUpgradeRequired,
        upgradeMessage,
        bidModalOpen,
        closeBidModal,
        openBidModal,
        placeBidPending,
        handleApproveBid,
        approveBidPending,
        handleDeclineBid,
        declineBidPending,
        handleStartChat,
        handleStateChange,
        patchStatePending,
        patchStateLeadId,
        canCreateLead,
        canPlaceBid,
        CATEGORY_OPTIONS,
        formatCurrencyInput,
        parseCurrencyInput,
    } = useOpenMarketLeads();

    useEffect(() => {
        if (leadIdFromUrl) {
            openPanel(leadIdFromUrl);
        } else {
            closePanel();
        }
    }, [leadIdFromUrl, openPanel, closePanel]);

    const handleOpenPanel = (id) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('leadId', id);
            return next;
        });
        openPanel(id);
    };

    const handleClosePanel = () => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('leadId');
            return next;
        });
        closePanel();
    };

    if (isUpgradeRequired) {
        return <OpenMarketUpgradeView upgradeMessage={upgradeMessage} />;
    }

    if (selectedLeadId) {
        return (
            <ListBodyLayout title="Open Market" description="Review lead details and manage bids">
                <Container $display="flex" $flexDirection="column" $gap="16px" $width="100%">
                    <Button
                        type="button"
                        $primary={false}
                        $size="14px"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleClosePanel}
                        style={{ alignSelf: 'flex-start' }}
                    >
                        Back to overview
                    </Button>
                    <OpenMarketLeadDetailPanel
                        leadId={selectedLeadId}
                        myBid={getLeadById(selectedLeadId)?.myBid ?? null}
                        onPlaceBid={canPlaceBid ? openBidModal : undefined}
                        onApproveBid={handleApproveBid}
                        approveBidPending={approveBidPending}
                        onDeclineBid={handleDeclineBid}
                        declineBidPending={declineBidPending}
                        onStartChat={handleStartChat}
                        onStateChange={handleStateChange}
                        stateChangePending={patchStatePending}
                        stateChangingLeadId={patchStateLeadId}
                    />
                    <AppModal
                        size="md"
                        title="Create Lead"
                        open={createModalOpen}
                        onCancel={() => {
                            setCreateModalOpen(false);
                            form.resetFields();
                        }}
                        footer={null}
                        styles={{ body: { paddingTop: 8 } }}
                    >
                        <CreateRoleModalBody>
                            <Form form={form} layout="vertical" initialValues={{ category: 'web_app' }}>
                                <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Enter title' }]}>
                                    <Input placeholder="e.g. Web app" />
                                </Form.Item>
                                <Form.Item
                                    name="description"
                                    label="Description"
                                    rules={[{ required: true, message: 'Enter description' }]}
                                >
                                    <Input.TextArea rows={4} placeholder="Describe your lead requirements" />
                                </Form.Item>
                                <Form.Item
                                    name="budgetMin"
                                    label="Min budget"
                                    rules={[{ required: true, message: 'Enter min budget' }]}
                                >
                                    <InputNumber min={0} placeholder="5000" />
                                </Form.Item>
                                <Form.Item
                                    name="budgetMax"
                                    label="Max budget"
                                    rules={[{ required: true, message: 'Enter max budget' }]}
                                >
                                    <InputNumber min={0} placeholder="15000" />
                                </Form.Item>
                                <Form.Item
                                    name="category"
                                    label="Category"
                                    rules={[{ required: true, message: 'Select category' }]}
                                >
                                    <Select options={CATEGORY_OPTIONS} placeholder="Select category" />
                                </Form.Item>
                            </Form>
                        </CreateRoleModalBody>
                        <CreateRoleModalFooter>
                            <Button
                                $primary={false}
                                $size="14px"
                                onClick={() => {
                                    setCreateModalOpen(false);
                                    form.resetFields();
                                }}
                            >
                                Cancel
                            </Button>
                            {canCreateLead && (
                                <Button
                                    $primary
                                    $blackOnLight
                                    $size="14px"
                                    onClick={onSubmitCreate}
                                    loading={createLeadPending}
                                >
                                    Create
                                </Button>
                            )}
                        </CreateRoleModalFooter>
                    </AppModal>
                    <AppModal
                        size="md"
                        title="Place Bid"
                        open={bidModalOpen}
                        onCancel={() => {
                            closeBidModal();
                            bidForm.resetFields();
                        }}
                        footer={null}
                        styles={{ body: { paddingTop: 8 } }}
                    >
                        <CreateRoleModalBody>
                            <Form form={bidForm} layout="vertical">
                                <Form.Item
                                    name="amount"
                                    label="Amount"
                                    rules={[{ required: true, message: 'Enter bid amount' }]}
                                >
                                    <InputNumber
                                        min={0}
                                        placeholder="$ 0"
                                        formatter={formatCurrencyInput}
                                        parser={parseCurrencyInput}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="proposal"
                                    label="Proposal"
                                    rules={[{ required: true, message: 'Enter proposal' }]}
                                >
                                    <Input.TextArea rows={3} placeholder="e.g. We can deliver in 8 weeks" />
                                </Form.Item>
                            </Form>
                        </CreateRoleModalBody>
                        <CreateRoleModalFooter>
                            <Button
                                $primary={false}
                                $size="14px"
                                onClick={() => {
                                    closeBidModal();
                                    bidForm.resetFields();
                                }}
                            >
                                Cancel
                            </Button>
                            {canPlaceBid && (
                                <Button $primary $blackOnLight $size="14px" onClick={onSubmitBid} loading={placeBidPending}>
                                    Place Bid
                                </Button>
                            )}
                        </CreateRoleModalFooter>
                    </AppModal>
                </Container>
            </ListBodyLayout>
        );
    }

    return (
        <ListBodyLayout
            title="Open Market"
            description="Overview of leads and submissions"
            actions={
                canCreateLead && (
                    <Button
                        $primary
                        $blackOnLight
                        icon={<PlusOutlined />}
                        onClick={() => setCreateModalOpen(true)}
                        $padding="10px 20px"
                        $size="14px"
                    >
                        Create Lead
                    </Button>
                )
            }
            kpiCards={[
                {
                    title: 'Total Leads',
                    value: total,
                    change: { value: '', isPositive: true, text: 'Open market' },
                },
                {
                    title: 'Open',
                    value: openCount,
                    change: { value: '', isPositive: true, text: 'Available leads' },
                },
                {
                    title: 'Closed',
                    value: closedCount,
                    change: { value: '', isPositive: true, text: 'Closed leads' },
                },
                {
                    title: 'Leads',
                    value: tableData.length,
                    change: { value: '', isPositive: true, text: 'Current filter' },
                },
            ]}
        >
            {leadsError && (
                <ErrorBlock>
                    Unable to load leads. Check that the Open Market API (GET /api/v1/open-market/leads) is available.
                </ErrorBlock>
            )}

            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="16px"
            >
                <Container $display="flex" $alignItems="center" $minWidth="0">
                    <RolesSliderTabWrap>
                        <SliderTab
                            items={[
                                { key: STATUS_TAB.ALL, label: 'All' },
                                { key: STATUS_TAB.OPEN, label: 'Open' },
                                { key: STATUS_TAB.CLOSED, label: 'Closed' },
                            ]}
                            activeKey={statusTab}
                            onChange={setStatusTab}
                            fitContent
                        />
                    </RolesSliderTabWrap>
                </Container>
                <Container $display="flex" $alignItems="center" $gap="10px">
                    <RolesSearchWrapper>
                        <SearchOutlined />
                        <RolesSearchInput
                            placeholder="Search here"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </RolesSearchWrapper>
                    <RolesFilterBtn type="button">
                        <FilterOutlined />
                        Filter
                    </RolesFilterBtn>
                </Container>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                pageSize={10}
                $topMargin={false}
                $radius={12}
                onRow={(record) => ({
                    onClick: () => handleOpenPanel(record.id),
                    style: { cursor: 'pointer' },
                })}
            />

            <AppModal
                size="md"
                title="Create Lead"
                open={createModalOpen}
                onCancel={() => {
                    setCreateModalOpen(false);
                    form.resetFields();
                }}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={form} layout="vertical" initialValues={{ category: 'web_app' }}>
                        <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Enter title' }]}>
                            <Input placeholder="e.g. Web app" />
                        </Form.Item>
                        <Form.Item
                            name="description"
                            label="Description"
                            rules={[{ required: true, message: 'Enter description' }]}
                        >
                            <Input.TextArea rows={4} placeholder="Describe your lead requirements" />
                        </Form.Item>
                        <Form.Item
                            name="budgetMin"
                            label="Min budget"
                            rules={[{ required: true, message: 'Enter min budget' }]}
                        >
                            <InputNumber min={0} placeholder="5000" />
                        </Form.Item>
                        <Form.Item
                            name="budgetMax"
                            label="Max budget"
                            rules={[{ required: true, message: 'Enter max budget' }]}
                        >
                            <InputNumber min={0} placeholder="15000" />
                        </Form.Item>
                        <Form.Item
                            name="category"
                            label="Category"
                            rules={[{ required: true, message: 'Select category' }]}
                        >
                            <Select options={CATEGORY_OPTIONS} placeholder="Select category" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        $primary={false}
                        $size="14px"
                        onClick={() => {
                            setCreateModalOpen(false);
                            form.resetFields();
                        }}
                    >
                        Cancel
                    </Button>
                    {canCreateLead && (
                        <Button
                            $primary
                            $blackOnLight
                            $size="14px"
                            onClick={onSubmitCreate}
                            loading={createLeadPending}
                        >
                            Create
                        </Button>
                    )}
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title="Place Bid"
                open={bidModalOpen}
                onCancel={() => {
                    closeBidModal();
                    bidForm.resetFields();
                }}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={bidForm} layout="vertical">
                        <Form.Item
                            name="amount"
                            label="Amount"
                            rules={[{ required: true, message: 'Enter bid amount' }]}
                        >
                            <InputNumber
                                min={0}
                                placeholder="$ 0"
                                formatter={formatCurrencyInput}
                                parser={parseCurrencyInput}
                            />
                        </Form.Item>
                        <Form.Item
                            name="proposal"
                            label="Proposal"
                            rules={[{ required: true, message: 'Enter proposal' }]}
                        >
                            <Input.TextArea rows={3} placeholder="e.g. We can deliver in 8 weeks" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        $primary={false}
                        $size="14px"
                        onClick={() => {
                            closeBidModal();
                            bidForm.resetFields();
                        }}
                    >
                        Cancel
                    </Button>
                    {canPlaceBid && (
                        <Button $primary $blackOnLight $size="14px" onClick={onSubmitBid} loading={placeBidPending}>
                            Place Bid
                        </Button>
                    )}
                </CreateRoleModalFooter>
            </AppModal>
        </ListBodyLayout>
    );
};

export default OpenMarket;
